//this file exists to avoid circular dependencies

import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { context } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { getCurrentUserServer, loadRawEntityDataByIDOrFail, loadRawEntityDataByIDs } from '@adornis/bh-util/util.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { Participation } from '../entities/Participation.js';
import { Product } from '../entities/products.js';
import { LECTURER_PERMISSIONS, Permission } from '../enums/roles.js';
import { mongo } from '../server/mongo.js';

// TODO: remove the server context support again. This function should ideally only be used on the client
export const getParticipationByID = registerQuery({
  operationName: 'getParticipationByID',
  type: () => Participation,
  params: [{ name: 'id', type: () => String }],
  resolve:
    (id: string) =>
    async <Tfields extends BaseQLSelectionSet<Participation>>(
      qglFields: Tfields,
    ): Promise<NarrowedEntity<Participation, Tfields>> => {
      const user = await getCurrentUserServer();
      if (!context.serverContext && !user) throw new Error('you are not authenticated');

      const data = await loadRawEntityDataByIDOrFail(Participation, id);

      if (context.serverContext) return data as any;
      if (!user) throw new Error('unreachable');

      if (user._id === data.userID) return data as any;
      if (user.isSuperAdmin) return data as any;

      const product = await loadRawEntityDataByIDOrFail(Product, data.productID, {
        institutionID: 1,
      });
      if (user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_USERS, product.institutionID)) return data as any;
      if (user.hasPermission(LECTURER_PERMISSIONS.MANAGE_PARTICIPATION as any, product.institutionID))
        return data as any;
      throw new Error('You dont have enough permission!');
    },
});

/**
 * Given the institutionIDs of the products referenced by the participations under consideration,
 * returns the subset of those institutionIDs the user is allowed to act on, per `checkPermission`.
 */
export function computeAllowedInstitutionIDs(
  institutionIDs: string[],
  checkPermission: (institutionID: string) => boolean,
): string[] {
  return Array.from(new Set(institutionIDs)).filter(checkPermission);
}

/**
 * Builds the final $match filter for getParticipationsByID: the requested ids, restricted to either
 * the requesting user's own participations, or participations whose product belongs to one of the
 * allowed institutions.
 */
export function buildParticipationsAccessFilter(params: {
  ids: string[];
  userID: string;
  allowedProductIDs: string[];
}): Record<string, unknown> {
  return {
    _id: { $in: params.ids },
    $or: [{ userID: params.userID }, { productID: { $in: params.allowedProductIDs } }],
  };
}

export const getParticipationsByID = registerQuery({
  operationName: 'getParticipationsByID',
  type: () => [Participation],
  params: [{ name: 'ids', type: () => [String] }],
  resolve: (ids: string[]) => async (qglFields: BaseQLSelectionSet<Participation>) => {
    const user = await getCurrentUserServer();
    const collectioin = mongo().collection(Participation._collectionName);
    const aggregationFilter2 = await (async () => {
      if (context.serverContext) return {};
      if (!user) throw new ExpectedError('not authenticated');
      if (user.isSuperAdmin) return {};
      if (!user.isAdmin && !user.isLecturer) return { userID: user._id };

      // Institution admin or lecturer: resolve which of the requested participations' products they
      // may act on, mirroring getParticipationByID's per-item hasPermission check above, but batched.
      const participationsRaw = await loadRawEntityDataByIDs(Participation, ids, { productID: 1 });
      const productIDs = Array.from(new Set(participationsRaw.map(p => p.productID).filter(Boolean) as string[]));
      const products =
        productIDs.length > 0 ? await loadRawEntityDataByIDs(Product, productIDs, { _id: 1, institutionID: 1 }) : [];
      const institutionIDs = products.map(p => p.institutionID).filter(Boolean) as string[];

      const allowedInstitutionIDs = user.isAdmin
        ? computeAllowedInstitutionIDs(institutionIDs, institutionID =>
            user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_USERS, institutionID),
          )
        : computeAllowedInstitutionIDs(institutionIDs, institutionID =>
            user.hasPermission(LECTURER_PERMISSIONS.MANAGE_PARTICIPATION as any, institutionID),
          );

      const allowedProductIDs = products.filter(p => allowedInstitutionIDs.includes(p.institutionID)).map(p => p._id);

      return buildParticipationsAccessFilter({ ids, userID: user._id, allowedProductIDs });
    })();

    const data = await collectioin
      .aggregate([{ $match: { _id: { $in: ids } } }, { $match: aggregationFilter2 }, { $project: qglFields }])
      .toArray();
    return data as Participation[];
  },
});
